/* screens/restaurant/DocumentsTab.jsx — База знаний: загрузка/управление файлами,
   AI-резюме (двухуровневая память). Боевой бэкенд /api/workspace-documents/:
   список+квота приходят пропсами (BusinessKnowledgeTab → listDocumentsReal); мутации — реальные
   (createDocumentReal multipart → streamDocumentSummary SSE → updateDocumentReal/deleteDocumentReal),
   локальный стейт docs — оптимистичное зеркало ответов сервера. Резюмирование — настоящий SSE-стрим
   (delta/status/done/error), не таймер. Удаление — через единый ConfirmDialog.
   Опц. wsId скоупит ВСЕ запросы на выбранный бизнес-ws (X-Workspace-Id без переключения). */

import React from 'react';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { DocumentCard } from '../../components/patterns/DocumentCard.jsx';
import { DropZone } from '../../components/patterns/DropZone.jsx';
import { EmptyState } from '../../components/patterns/States.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { QuotaBar } from '../../components/ui/QuotaBar.jsx';
import { DOC_KIND_OPTIONS } from '../../lib/adapters/knowledge.js';
import { aiClosedDetail } from '../../lib/aiAccess.js';
import {
  createDocumentReal, updateDocumentReal, deleteDocumentReal, streamDocumentSummary,
} from '../../lib/api/index.js';
import { RR } from '../../lib/router.jsx';

  const { useState, useEffect, useRef, useCallback } = React;
  const { useSearchParams } = RR;

  function DocumentsTab({ quota, documents, wsId }) {
    const confirm = useConfirm();
    const toast = useToast();
    const [params] = useSearchParams();
    const highlightId = params.get('doc');

    const [docs, setDocs] = useState(documents || []);
    const [uploads, setUploads] = useState([]);  // { id, name, size, pct, error?, _file }
    const uploadsRef = useRef([]);                // зеркало uploads для retry (без вложенного setState)
    const [uploadError, setUploadError] = useState(null);  // ошибка квоты/аплоада (inline под DropZone)
    const streamsRef = useRef({});                // id → cancel() активного SSE-резюмирования
    const upTimers = useRef({});                  // id → interval анимации прогресса аплоада

    useEffect(() => { uploadsRef.current = uploads; }, [uploads]);

    useEffect(() => { setDocs(documents || []); }, [documents]);

    const patchDoc = useCallback((id, patch) =>
      setDocs((list) => list.map((d) => (d.id === id ? { ...d, ...patch } : d))), []);

    // на unmount — оборвать все активные стримы и аниматоры прогресса
    useEffect(() => () => {
      Object.values(streamsRef.current).forEach((fn) => { try { fn(); } catch { /* noop */ } });
      streamsRef.current = {};
      Object.values(upTimers.current).forEach(clearInterval);
      upTimers.current = {};
    }, []);

    // живая квота (считаем из текущих документов; лимиты — из пропса, -1 уже → null)
    const usedCount = docs.length;
    const usedMb = docs.reduce((s, d) => s + (d.size_bytes || 0), 0) / 1_000_000;

    // запустить реальный SSE-стрим резюмирования документа
    const beginSummarize = useCallback((id) => {
      if (streamsRef.current[id]) { try { streamsRef.current[id](); } catch { /* noop */ } }
      // summary_note (резюме ждёт тарифа) снимаем сразу: новый запуск — уже
      // другой ответ бэка, и в оплаченном пространстве пометка врала бы.
      patchDoc(id, { summary: '', summary_status: 'processing', summary_error: null, summary_note: null });
      streamsRef.current[id] = streamDocumentSummary(id, {
        onStatus: (status) => patchDoc(id, { summary_status: status }),
        onDelta: (text) => setDocs((list) =>
          list.map((d) => (d.id === id ? { ...d, summary: (d.summary || '') + text } : d))),
        onDone: (doc) => {
          setDocs((list) => list.map((d) => (d.id === id ? doc : d)));
          delete streamsRef.current[id];
        },
        onError: (err) => {
          // Пространство без тарифа: доступ к модели закрыт (402 / кадр
          // subscription_expired). Документ сохранён — это не сбой, а ожидание
          // тарифа: нейтральная пометка на карточке вместо красного «Ошибка».
          const closed = aiClosedDetail(err);
          if (closed) {
            patchDoc(id, { summary: '', summary_status: 'pending', summary_error: null, summary_note: closed });
            toast.show('Документ сохранён. После выбора тарифа нажмите «Сгенерировать резюме».', 'muted');
            delete streamsRef.current[id];
            return;
          }
          patchDoc(id, { summary_status: 'failed', summary_error: (err && err.message) || 'Ошибка' });
          toast.show((err && err.message) || 'Не удалось сгенерировать резюме.', 'error');
          delete streamsRef.current[id];
        },
      }, wsId);
    }, [patchDoc, toast, wsId]);

    // реальная multipart-загрузка одного файла; прогресс анимируем (fetch не даёт upload-progress) до 90%,
    // 100% — по ответу сервера, затем чип → карточка документа + авто-резюмирование.
    const startUpload = useCallback((up) => {
      setUploads((u) => [...u.filter((x) => x.id !== up.id), { ...up, pct: 6, error: null }]);
      const stop = () => {
        if (upTimers.current[up.id]) { clearInterval(upTimers.current[up.id]); delete upTimers.current[up.id]; }
      };
      upTimers.current[up.id] = setInterval(() => setUploads((list) => {
        const cur = list.find((x) => x.id === up.id);
        if (!cur || cur.error || cur.pct >= 90) return list;
        return list.map((x) => (x.id === up.id ? { ...x, pct: Math.min(90, x.pct + 7 + Math.random() * 10) } : x));
      }), 180);

      createDocumentReal(up._file, undefined, wsId).then((res) => {
        stop();
        if (!res.ok) {
          const msg = (res.data && res.data.detail) || 'Не удалось загрузить файл.';
          setUploads((list) => list.map((x) => (x.id === up.id ? { ...x, error: msg } : x)));
          return;
        }
        const doc = res.data;
        setUploads((list) => list.map((x) => (x.id === up.id ? { ...x, pct: 100 } : x)));
        setTimeout(() => {
          setUploads((l) => l.filter((x) => x.id !== up.id));
          setDocs((d) => [doc, ...d.filter((x) => x.id !== doc.id)]);
          // авто-резюмирование сразу после загрузки (как в боевом DocumentsTab)
          if (doc.summary_status === 'pending' || (doc.is_image && doc.summary_status !== 'failed')) {
            beginSummarize(doc.id);
          }
        }, 200);
      }).catch((err) => {
        stop();
        setUploads((list) => list.map((x) => (x.id === up.id
          ? { ...x, error: (err && err.message) || 'Не удалось загрузить файл.' } : x)));
      });
    }, [beginSummarize, wsId]);

    // повтор сбойного аплоада (читаем File из ref — без вложенного setState)
    const retryUpload = (id) => {
      const up = uploadsRef.current.find((x) => x.id === id);
      if (up && up._file) startUpload({ id: up.id, name: up.name, size: up.size, _file: up._file });
    };

    // ── загрузка: клиентская проверка квоты количества/объёма до старта (бэк дублирует 402) ──
    const onFiles = (files) => {
      setUploadError(null);
      const remainingCount = (quota.limit_count ?? Infinity) - usedCount - uploads.length;
      if (files.length > remainingCount) {
        setUploadError(remainingCount <= 0
          ? `Достигнут лимит файлов (${quota.limit_count}). Удалите ненужные документы, чтобы загрузить новые.`
          : `Можно добавить ещё ${remainingCount} файлов из лимита ${quota.limit_count} — выбрано ${files.length}.`);
        return;
      }
      const addMb = files.reduce((s, f) => s + (f.size || 0), 0) / 1_000_000;
      if (quota.limit_mb != null && usedMb + addMb > quota.limit_mb) {
        setUploadError(`Превышен лимит объёма базы знаний (${quota.limit_mb} МБ). Сейчас занято ${usedMb.toFixed(1).replace('.', ',')} МБ.`);
        return;
      }
      const ts = Date.now();
      files.forEach((f, i) => startUpload({ id: 'up-' + ts + '-' + i, name: f.name, size: f.size, _file: f }));
    };

    // ── действия карточки (реальные мутации) ──
    const onChangeKind = async (id, kind) => {
      const res = await updateDocumentReal(id, { kind }, wsId);
      if (res.ok) setDocs((list) => list.map((d) => (d.id === id ? res.data : d)));
      else toast.show((res.data && res.data.detail) || 'Не удалось обновить тип.', 'error');
    };
    const onRegenerate = (id) => beginSummarize(id);
    const onRetry = (id) => beginSummarize(id);
    const onSaveSummary = async (id, text) => {
      const res = await updateDocumentReal(id, { summary: text }, wsId);
      if (res.ok) {
        setDocs((list) => list.map((d) => (d.id === id ? res.data : d)));
        toast.show('Резюме обновлено', 'success');
      } else {
        toast.show((res.data && res.data.detail) || 'Не удалось сохранить резюме.', 'error');
      }
    };
    const onDelete = async (doc) => {
      const ok = await confirm({
        title: 'Удалить документ?',
        body: `Файл «${doc.filename}» и его резюме будут удалены из базы знаний. Действие необратимо.`,
        confirmLabel: 'Удалить', cancelLabel: 'Отмена', tone: 'danger',
      });
      if (!ok) return;
      if (streamsRef.current[doc.id]) { try { streamsRef.current[doc.id](); } catch { /* noop */ } delete streamsRef.current[doc.id]; }
      const res = await deleteDocumentReal(doc.id, wsId);
      if (res.ok) {
        setDocs((list) => list.filter((d) => d.id !== doc.id));
        toast.show('Документ удалён', 'muted');
      } else {
        toast.show((res.data && res.data.detail) || 'Не удалось удалить документ.', 'error');
      }
    };

    return (
      <div className="kb stack stack-16">
        {/* введение: двухуровневая память + квота */}
        <section className="kb-intro">
          <div className="kb-intro__text">
            <h2 className="t-h3">База знаний</h2>
            <p className="t-body-sm text-dim">
              Резюме каждого файла всегда в контексте ассистента. Полный файл подгружается
              по запросу (<span className="num">deep_readable</span>) — если формат читается напрямую.
            </p>
          </div>
          <div className="kb-quota">
            <QuotaBar icon="file" label="Файлы" used={usedCount} limit={quota.limit_count} />
            <QuotaBar icon="database" label="Объём" used={usedMb} limit={quota.limit_mb} unit="МБ" />
          </div>
        </section>

        <DropZone onFiles={onFiles} uploads={uploads} error={uploadError}
          onReject={setUploadError} onRetryUpload={retryUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.rtf,image/*" fileLimitMb={quota.file_limit_mb} />

        {docs.length === 0 && uploads.length === 0 ? (
          <EmptyState icon="database" title="База знаний пуста"
            body="Загрузите меню, стандарты, прайс-листы поставщиков — ассистент будет учитывать их при расчётах и ответах."
            serif={false} />
        ) : (
          <div className="doc-grid">
            {docs.map((d) => (
              <DocumentCard key={d.id} doc={d} kindOptions={DOC_KIND_OPTIONS}
                highlighted={String(d.id) === highlightId}
                onChangeKind={onChangeKind} onDelete={onDelete}
                onRegenerate={onRegenerate} onRetry={onRetry} onSaveSummary={onSaveSummary} />
            ))}
          </div>
        )}
      </div>
    );
  }

export { DocumentsTab };
